import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './PlanCheckout.css';
import { CheckCircle, ShieldTick, XClose } from '@untitledui/icons';
import { listMembershipPlans } from '../../../services/membershipsApi';
import { setCompanyMembershipPlan } from '../../../services/companyApi';

const PRICE_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PAYMENT_METHODS = [
  {
    id: 'corporate-card',
    label: 'Tarjeta corporativa',
    helper: 'Visa terminación ••4242',
    details: 'Pagos recurrentes automáticos cada ciclo seleccionado.',
    icon: '💳',
    brands: ['VISA', 'MC'],
  },
  {
    id: 'bank-transfer',
    label: 'Transferencia SPEI',
    helper: 'Tiempo de acreditación 24-48 h',
    details: 'Recibirás instrucciones y referencia única para conciliar el pago.',
    icon: '🏦',
    brands: ['SPEI'],
  },
  {
    id: 'credits',
    label: 'Créditos disponibles',
    helper: 'Utiliza tu saldo PPC / Leads',
    details: 'Descuento automático del saldo disponible para planes elegibles.',
    icon: '⚡️',
    brands: ['Saldo'],
  },
];

const TAX_RATE = 0.16;

const STATUS = {
  REVIEW: 'review',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const formatPrice = value => {
  if (value === null || value === undefined) return 'Gratis';
  if (Number(value) === 0) return 'Gratis';
  return PRICE_FORMATTER.format(Number(value));
};

const formatCurrency = value => PRICE_FORMATTER.format(Number(value) || 0);

const getDisplayPrice = (plan, billingCycle) => {
  if (!plan) return { amount: 'Gratis', label: 'Plan gratuito' };
  const priceValue = billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  const label =
    priceValue === null || Number(priceValue) === 0
      ? 'Plan gratuito'
      : billingCycle === 'yearly'
      ? 'por año'
      : 'por mes';
  return {
    amount: formatPrice(priceValue),
    label,
    numeric: Number(priceValue) || 0,
  };
};

const buildPaymentMethodDisplay = methodId => {
  const method = PAYMENT_METHODS.find(option => option.id === methodId);
  if (!method) return 'No disponible';
  return method.helper ? `${method.label} · ${method.helper}` : method.label;
};

const buildReferenceCode = timestamp => {
  if (!timestamp) return '#SIM-0001';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '#SIM-0001';
  const base = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(
    date.getDate()
  ).padStart(2, '0')}`;
  return `#INV-${base}-${String(timestamp).slice(-4)}`;
};

const buildPlanSnapshot = plan => {
  if (!plan) return null;
  return {
    id: plan.id || plan.slug || '',
    slug: plan.slug || plan.id || '',
    name: plan.name || '',
    monthlyPrice: plan.monthlyPrice ?? null,
    yearlyPrice: plan.yearlyPrice ?? null,
    publicationsLimit:
      typeof plan.publicationsLimit === 'number' && Number.isFinite(plan.publicationsLimit)
        ? plan.publicationsLimit
        : 0,
    teamSeats:
      typeof plan.teamSeats === 'number' && Number.isFinite(plan.teamSeats) ? plan.teamSeats : 0,
    ppcCredits:
      typeof plan.ppcCredits === 'number' && Number.isFinite(plan.ppcCredits) ? plan.ppcCredits : 0,
    leadCredits:
      typeof plan.leadCredits === 'number' && Number.isFinite(plan.leadCredits)
        ? plan.leadCredits
        : 0,
    allowsPriorityFeed: Boolean(plan.allowsPriorityFeed),
    verificationIncluded: Boolean(plan.verificationIncluded),
    description: plan.description || '',
    theme: plan.theme && typeof plan.theme === 'object' ? { ...plan.theme } : null,
    features: Array.isArray(plan.features) ? [...plan.features] : [],
  };
};

const computeNextRenewalDate = (timestamp, billingCycle) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  if (billingCycle === 'yearly') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }
  return date.toISOString();
};

export default function PlanCheckout() {
  const { planSlug = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const preloadedPlan = location.state?.plan || null;
  const defaultCycle = location.state?.billingCycle || 'monthly';
  const companyId = location.state?.companyId || '';

  const [billingCycle, setBillingCycle] = useState(defaultCycle);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(!preloadedPlan);
  const [error, setError] = useState('');

  const [status, setStatus] = useState(STATUS.REVIEW);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('corporate-card');
  const [processingMessage, setProcessingMessage] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [updateRecord, setUpdateRecord] = useState(null);
  const isProcessing = status === STATUS.PROCESSING;

  useEffect(() => {
    let ignore = false;
    if (preloadedPlan) {
      setPlans([preloadedPlan]);
      setLoading(false);
      return () => {
        ignore = true;
      };
    }

    const loadPlans = async () => {
      setLoading(true);
      setError('');
      try {
        const fetched = await listMembershipPlans();
        if (!ignore) {
          setPlans(Array.isArray(fetched) ? fetched : []);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'No pudimos obtener la información del plan.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadPlans();

    return () => {
      ignore = true;
    };
  }, [preloadedPlan]);

  const selectedPlan = useMemo(() => {
    if (preloadedPlan) return preloadedPlan;
    if (!plans.length) return null;
    return (
      plans.find(
        plan => plan.slug === planSlug || plan.id === planSlug || plan.name?.toLowerCase() === planSlug
      ) || null
    );
  }, [planSlug, plans, preloadedPlan]);

  const priceInfo = useMemo(() => getDisplayPrice(selectedPlan, billingCycle), [selectedPlan, billingCycle]);

  const summaryTotals = useMemo(() => {
    const subtotal = priceInfo.numeric || 0;
    const tax = subtotal > 0 ? Number((subtotal * TAX_RATE).toFixed(2)) : 0;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  }, [priceInfo.numeric]);

  const simulatePayment = outcome => {
    if (!selectedPlan) return;
    setStatus(STATUS.PROCESSING);
    setPaymentError('');
    setProcessingMessage(
      outcome === 'success'
        ? 'Validando método de pago y activando beneficios...'
        : 'Simulando error de pago. Restableciendo estado...'
    );

    window.setTimeout(() => {
      if (outcome === 'success') {
        const record = {
          planId: selectedPlan.slug || selectedPlan.id,
          planName: selectedPlan.name,
          billingCycle,
          paymentMethod: selectedPaymentMethod,
          price: priceInfo.numeric,
          timestamp: new Date().toISOString(),
          planSnapshot: buildPlanSnapshot(selectedPlan),
        };

        const persistRecordLocally = () => {
          try {
            window.localStorage.setItem('flotte:lastPlanCheckout', JSON.stringify(record));
          } catch (storageError) {
            console.warn('No fue posible registrar el último checkout simulado.', storageError);
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('flotte:plan-updated', { detail: record }));
          }
        };

        const finalizeSuccess = () => {
          persistRecordLocally();
          setUpdateRecord(record);
          setStatus(STATUS.SUCCESS);
          setProcessingMessage('');
        };

        const handleSyncError = error => {
          console.error('No se pudo actualizar el plan de la empresa.', error);
          setPaymentError(
            'El pago se procesó, pero no pudimos activar el plan en tu empresa. Intenta nuevamente.'
          );
          setStatus(STATUS.FAILURE);
          setProcessingMessage('');
        };

        const syncCompanyPlan = async () => {
          if (!companyId) {
            finalizeSuccess();
            return;
          }
          try {
            const membershipRenewsAt = computeNextRenewalDate(record.timestamp, record.billingCycle);
            await setCompanyMembershipPlan(companyId, record.planId, {
              membershipRenewsAt,
              publicationLimit: Number(selectedPlan.publicationsLimit) || 0,
              userLimit: Number(selectedPlan.teamSeats) || 0,
              ppcCredits: Number(selectedPlan.ppcCredits) || 0,
              leadCredits: Number(selectedPlan.leadCredits) || 0,
              canPublish: true,
            });
            finalizeSuccess();
          } catch (syncError) {
            handleSyncError(syncError);
          }
        };

        syncCompanyPlan();
      } else {
        setPaymentError('El pago fue rechazado en la simulación. Verifica el método seleccionado e inténtalo de nuevo.');
        setStatus(STATUS.FAILURE);
        setProcessingMessage('');
      }
    }, 1600);
  };

  const resetSimulation = () => {
    setStatus(STATUS.REVIEW);
    setPaymentError('');
    setProcessingMessage('');
  };

  const handleBackToPlans = () => {
    navigate('/dashboard?section=suscripciones');
  };

  const handleDownloadReceipt = () => {
    try {
      if (typeof window !== 'undefined' && typeof window.print === 'function') {
        window.print();
      }
    } catch (err) {
      console.warn('No fue posible descargar el comprobante simulado.', err);
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="plan-checkout__loading">
          <span className="plan-checkout__skeleton plan-checkout__skeleton--title" />
          <span className="plan-checkout__skeleton plan-checkout__skeleton--line" />
          <span className="plan-checkout__skeleton plan-checkout__skeleton--line short" />
        </div>
      );
    }

    if (error || !selectedPlan) {
      return (
        <div className="plan-checkout__error">
          <XClose aria-hidden="true" />
          <div>
            <h1>No pudimos cargar el plan</h1>
            <p>{error || 'El plan solicitado no existe o fue retirado temporalmente.'}</p>
          </div>
          <button type="button" onClick={handleBackToPlans}>
            Volver a planes
          </button>
        </div>
      );
    }

    if (status === STATUS.SUCCESS) {
      const methodDisplay = buildPaymentMethodDisplay(updateRecord?.paymentMethod || selectedPaymentMethod);
      const receiptRows = [
        { label: 'Total pagado', value: formatCurrency(updateRecord?.price ?? summaryTotals.total) },
        { label: 'Plan', value: selectedPlan.name },
        { label: 'Método', value: methodDisplay },
        { label: 'Referencia', value: buildReferenceCode(updateRecord?.timestamp) },
      ];

      return (
        <section className="plan-checkout__result-shell">
          <div className="plan-checkout__result-card">
            <div className="plan-checkout__status-icon is-success">
              <CheckCircle aria-hidden="true" />
            </div>
            <div className="plan-checkout__status-copy">
              <h1>¡Pago Exitoso!</h1>
              <p>Tu plan Premium está activo.</p>
            </div>
            <div className="plan-checkout__receipt" aria-label="Resumen del pago">
              {receiptRows.map(row => (
                <div key={row.label} className="plan-checkout__receipt-row">
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
            <div className="plan-checkout__result-actions">
              <button type="button" className="plan-checkout__pay-btn" onClick={handleBackToPlans}>
                Ir al Dashboard
              </button>
              <button type="button" className="plan-checkout__ghost-btn" onClick={handleDownloadReceipt}>
                Descargar recibo
              </button>
              <button type="button" className="plan-checkout__link-btn" onClick={resetSimulation}>
                Simular nuevamente
              </button>
            </div>
          </div>
        </section>
      );
    }

    if (status === STATUS.FAILURE) {
      const methodDisplay = buildPaymentMethodDisplay(selectedPaymentMethod);
      const receiptRows = [
        { label: 'Total a pagar', value: formatCurrency(summaryTotals.total) },
        { label: 'Plan', value: selectedPlan?.name || 'No disponible' },
        { label: 'Método', value: methodDisplay },
        { label: 'Referencia', value: 'Pendiente' },
      ];

      return (
        <section className="plan-checkout__result-shell">
          <div className="plan-checkout__result-card">
            <div className="plan-checkout__status-icon is-failure">
              <XClose aria-hidden="true" />
            </div>
            <div className="plan-checkout__status-copy">
              <h1>No pudimos procesar el pago</h1>
              <p>Por favor, verifica tu método de pago.</p>
            </div>
            <div className="plan-checkout__receipt" aria-label="Detalle del intento">
              {receiptRows.map(row => (
                <div key={row.label} className="plan-checkout__receipt-row">
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </div>
              ))}
            </div>
            <div className="plan-checkout__result-actions">
              <button type="button" className="plan-checkout__pay-btn" onClick={resetSimulation}>
                Intentar de nuevo
              </button>
              <button type="button" className="plan-checkout__ghost-btn" onClick={handleBackToPlans}>
                Ir al Dashboard
              </button>
            </div>
          </div>
        </section>
      );
    }

    const planHighlights = [
      {
        label: 'Publicaciones activas',
        value: selectedPlan.publicationsLimit
          ? `${Number(selectedPlan.publicationsLimit).toLocaleString('es-MX')} publicaciones`
          : 'Ilimitadas',
      },
      {
        label: 'Usuarios de equipo',
        value: selectedPlan.teamSeats
          ? `${Number(selectedPlan.teamSeats).toLocaleString('es-MX')} usuarios`
          : 'Ilimitados',
      },
      {
        label: 'Créditos incluidos',
        value: `${Number(selectedPlan.ppcCredits || 0).toLocaleString('es-MX')} PPC · ${Number(
          selectedPlan.leadCredits || 0
        ).toLocaleString('es-MX')} Leads`,
      },
      {
        label: 'Verificación',
        value: selectedPlan.verificationIncluded ? 'Incluida' : 'Opcional',
      },
    ];
    const billingCopy = billingCycle === 'yearly' ? 'Facturado Anualmente' : 'Facturado Mensualmente';

    return (
      <>
        <section className="plan-checkout__intro">
          <div>
            <p className="plan-checkout__breadcrumb">Checkout de membresía</p>
            <h1>Confirmar suscripción Premium</h1>
            <p>
              Selecciona el método de pago y confirma la activación del plan <strong>{selectedPlan.name}</strong>.
            </p>
          </div>
        </section>

        <section className="plan-checkout__grid">
          <div className="plan-checkout__main" aria-label="Detalles del plan y métodos de pago">
            <article className="plan-checkout__card plan-checkout__plan-card">
              <div className="plan-checkout__plan-header">
                <div>
                  <p className="plan-checkout__subheading">Plan seleccionado</p>
                  <h2>{selectedPlan.name}</h2>
                </div>
                <label className="plan-checkout__cycle-control">
                  Ciclo de facturación
                  <select value={billingCycle} onChange={event => setBillingCycle(event.target.value)}>
                    <option value="monthly">Mensual</option>
                    <option value="yearly">Anual</option>
                  </select>
                </label>
              </div>

              <div className="plan-checkout__plan-price">
                <span>Precio del plan</span>
                <strong>{priceInfo.amount}</strong>
                <small>{billingCopy}</small>
              </div>

              <ul className="plan-checkout__feature-grid">
                {planHighlights.map(item => (
                  <li key={item.label}>
                    <span className="plan-checkout__feature-value">{item.value}</span>
                    <span className="plan-checkout__feature-label">{item.label}</span>
                  </li>
                ))}
              </ul>

              {Array.isArray(selectedPlan.features) && selectedPlan.features.length ? (
                <div className="plan-checkout__included">
                  <h3>Beneficios incluidos</h3>
                  <ul>
                    {selectedPlan.features.map(item => (
                      <li key={item}>
                        <ShieldTick aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </article>

            <article className="plan-checkout__card plan-checkout__payments-card">
              <div className="plan-checkout__payments-head">
                <p className="plan-checkout__subheading">Método de pago</p>
                <h2>Selecciona una opción</h2>
                <p>Elige la forma de pago que usarás para activar o simular la suscripción.</p>
              </div>

              <div className="plan-checkout__payment-group" role="radiogroup" aria-label="Método de pago">
                {PAYMENT_METHODS.map(option => {
                  const isDisabled =
                    option.id === 'credits' && (!selectedPlan.ppcCredits || selectedPlan.ppcCredits === 0);
                  const isSelected = selectedPaymentMethod === option.id;
                  const classes = [
                    'plan-checkout__payment-card',
                    isSelected ? 'is-selected' : '',
                    isDisabled ? 'is-disabled' : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <label key={option.id} className={classes} htmlFor={`payment-${option.id}`}>
                      <input
                        id={`payment-${option.id}`}
                        type="radio"
                        name="payment-method"
                        value={option.id}
                        checked={isSelected}
                        onChange={() => setSelectedPaymentMethod(option.id)}
                        className="plan-checkout__radio"
                        disabled={isDisabled}
                      />
                      <span className="plan-checkout__payment-icon" aria-hidden="true">
                        {option.icon}
                      </span>
                      <div className="plan-checkout__payment-body">
                        <div className="plan-checkout__payment-top">
                          <span className="plan-checkout__payment-title">{option.label}</span>
                          {Array.isArray(option.brands) && option.brands.length ? (
                            <span className="plan-checkout__brands">
                              {option.brands.map(brand => (
                                <span key={brand} className="plan-checkout__brand-pill">
                                  {brand}
                                </span>
                              ))}
                            </span>
                          ) : null}
                        </div>
                        <p className="plan-checkout__payment-helper">{option.helper}</p>
                        <p className="plan-checkout__payment-details">{option.details}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </article>
          </div>

          <aside className="plan-checkout__summary-card" aria-label="Resumen de compra">
            <div className="plan-checkout__summary-head">
              <p>Resumen del pedido</p>
              <p className="plan-checkout__summary-amount">{formatCurrency(summaryTotals.total)}</p>
              <span className="plan-checkout__summary-cycle">{billingCopy}</span>
            </div>

            <ul className="plan-checkout__breakdown">
              <li>
                <span>Subtotal</span>
                <span className="plan-checkout__breakdown-value">{formatCurrency(summaryTotals.subtotal)}</span>
              </li>
              <li>
                <span>Impuestos (IVA 16%)</span>
                <span className="plan-checkout__breakdown-value">{formatCurrency(summaryTotals.tax)}</span>
              </li>
            </ul>

            <div className="plan-checkout__summary-total">
              <span>Total estimado</span>
              <strong>{formatCurrency(summaryTotals.total)}</strong>
            </div>

            {processingMessage ? <p className="plan-checkout__processing">{processingMessage}</p> : null}
            {paymentError ? <p className="plan-checkout__error-text">{paymentError}</p> : null}

            <button
              type="button"
              className="plan-checkout__pay-btn"
              onClick={() => simulatePayment('success')}
              disabled={isProcessing}
            >
              🔒 Pagar y activar
            </button>

            <div className="plan-checkout__summary-actions">
              <button
                type="button"
                className="plan-checkout__ghost-btn"
                onClick={() => simulatePayment('failure')}
                disabled={isProcessing}
              >
                Simular pago con error
              </button>
              <button type="button" className="plan-checkout__link-btn" onClick={handleBackToPlans}>
                Volver a planes
              </button>
            </div>
          </aside>
        </section>
      </>
    );
  };

  return <main className="plan-checkout">{renderBody()}</main>;
}
